import { ApplicationStatus, BaseAPIResponse, CreateApplicationInput, UIExtendedApplication } from 'types';
import ApplicationDrawerHeaderFooter from './ApplicationDrawerHeaderFooter';
import JobDescription from '../job/postionDrawer/JobDescription';
import { Drawer } from '../shared/Drawer';
import { ApplicationDrawerDetail } from 'src/pages/MyApplications';
import { UseMutationResult } from 'react-query';
import Container from '../shared/Container';

// Ongoing application statuses
const ongoingStatuses = [
	ApplicationStatus.SHORTLISTED,
	ApplicationStatus.INTERVIEW_SCHEDULED,
	ApplicationStatus.ASSIGNMENT_IN_PROGRESS,
	ApplicationStatus.OFFER_ROLLED_OUT,
	ApplicationStatus.WAITING_FOR_RECRUITER_ACTION,
	ApplicationStatus.QUALIFIED_ALL_ROUNDS,
	ApplicationStatus.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL,
	ApplicationStatus.PENDING_FOR_OFFER_LETTER_APPROVAL,
	ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL,
	ApplicationStatus.CHANGED_OFFER_TERMS,
];

interface Props {
	isApplicationDrawerOpen: boolean;
	setIsApplicationDrawerOpen: (value: boolean) => void;
	detail: ApplicationDrawerDetail;
	showCta: boolean;
	myApplicationStatus: ApplicationStatus;
	onApplyModalOpen: () => void;
	setIsLinkedInResumeModalOpen: (value: boolean) => void;
	setCurrentlySelectedApplication: (arg: UIExtendedApplication) => void;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	application: UIExtendedApplication;
}

const ApplicationDrawer = ({
	isApplicationDrawerOpen,
	setIsApplicationDrawerOpen,
	detail,
	showCta,
	onApplyModalOpen,
	myApplicationStatus,
	setIsLinkedInResumeModalOpen,
	setCurrentlySelectedApplication,
	createApplicationMutation,
	application,
}: Props) => {
	// Check if we should show the links
	const shouldShowLinks =
		application.status === ApplicationStatus.APPLIED_BY_JOBSEEKER ||
		ongoingStatuses.includes(application.status as ApplicationStatus);

	// Get company pre-placement call recording
	const companyPrePlacementLink = application.position?.company?.prePlacementCallRecording;
	const showCompanyLink = application.position?.showPrePlacementCallRecordingToStudent;

	// Get position prep material
	const prepMaterialLinks = application.position?.prepMaterial || [];
	const showPrepMaterial = application.position?.showPrepMaterialToStudent;

	return (
		<Drawer
			isOpen={isApplicationDrawerOpen}
			onClose={() => setIsApplicationDrawerOpen(false)}
			header={
				<Container className="!px-0 xl:!px-[80px]">
					<div className="md:mt-5 px-2 xl:px-0 py-[10px]">
						<ApplicationDrawerHeaderFooter
							company={detail}
							isLoadingApplying={detail.isLoadingApplying}
							isFooter={false}
							showCta={showCta}
							setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
							myApplicationStatus={myApplicationStatus}
							setIsApplicationDrawerOpen={setIsApplicationDrawerOpen}
							setCurrentlySelectedApplication={setCurrentlySelectedApplication}
							createApplicationMutation={createApplicationMutation}
							application={application}
							hasBond={detail?.hasBond}
						/>
					</div>
					<div className="flex justify-center">
						<hr className="h-[1px] w-full rounded-[32px] border-0 bg-[#C7C9D9] dark:bg-[#8F90A6]" />
					</div>
				</Container>
			}
			body={
				<Container>
					<div className="w-full mt-[20px] md:mt-0">
						<JobDescription
							workingMode={detail?.workingMode}
							workingExp={detail.workingExp + ' Yrs'}
							minSalary={detail?.minSalary}
							maxSalary={detail?.maxSalary}
							location={detail?.location}
							jobType={detail?.jobType}
							description={detail?.description}
							minInternshipSalary={detail?.minInternshipSalary}
							maxInternshipSalary={detail?.maxInternshipSalary}
							internshipDuration={detail?.internshipDuration}
							hasBond={detail?.hasBond}
							bondDetails={detail?.bondDetails}
						/>
					</div>

					{/* Pre-Placement Resources Section */}
					{shouldShowLinks && (companyPrePlacementLink && showCompanyLink || (prepMaterialLinks.length > 0 && showPrepMaterial)) && (
						<div className="w-full mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
							<h3 className="text-lg font-semibold mb-4 dark:text-white">Pre-Placement Resources</h3>

							{/* Company Pre-Placement Call Recording */}
							{companyPrePlacementLink && showCompanyLink && (
								<div className="mb-4">
									<h4 className="text-sm font-medium mb-2 dark:text-gray-300">Pre-Placement Call Recording</h4>
									<a
										href={companyPrePlacementLink}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 dark:text-blue-400 hover:underline break-all"
									>
										{companyPrePlacementLink}
									</a>
								</div>
							)}

							{/* Prep Material Links */}
							{prepMaterialLinks.length > 0 && showPrepMaterial && (
								<div>
									<h4 className="text-sm font-medium mb-2 dark:text-gray-300">Prep Material</h4>
									<ul className="space-y-2">
										{prepMaterialLinks.map((link, index) => (
											<li key={index}>
												<a
													href={link}
													target="_blank"
													rel="noopener noreferrer"
													className="text-blue-600 dark:text-blue-400 hover:underline break-all"
												>
													{link}
												</a>
											</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					<div className="ml-[66px] w-[420px] sm:ml-0 sm:mt-6 sm:w-full">{null}</div>
				</Container>
			}
			footer={
				<div
					style={{
						display: showCta ? '' : 'none',
					}}
					className="dark:bg-[#111E2C] p-3 md:hidden dark:shadow-[inset_0px_5px_5px_-5px_rgba(0,_0,_0,_1)]"
				>
					<ApplicationDrawerHeaderFooter
						company={detail}
						setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
						isLoadingApplying={detail.isLoadingApplying}
						isFooter={true}
						showCta={showCta}
						myApplicationStatus={myApplicationStatus}
						setIsApplicationDrawerOpen={setIsApplicationDrawerOpen}
						setCurrentlySelectedApplication={setCurrentlySelectedApplication}
						createApplicationMutation={createApplicationMutation}
						application={application}
					/>
				</div>
			}
		/>
	);
};

export default ApplicationDrawer;
